import { generatePath } from "react-router";
import { BASE_URL as backendBaseUrl } from "./api";
import { appendQueryString } from "./api/appendQueryString";
import type { LocationOverviewDataQueryParams } from "./components/AdminDashboard/LocationOverview/LocationOverview.types";
import type { AusfallarbeitTimeSeriesQueryParams } from "./components/RevenueMonitoring/RevenueMonitoring.types.ts";
import type { TodoUserDecision } from "./components/Todos/Todos.types";
import { startPageApi } from "./modules/StartPage/api/startPageApi";
import { ROUTES } from "./routes";
import type { SiteLicense } from "./types/api.types";
import type { TodoCategory } from "./utils/backend-types";
import { Unit } from "./utils/enums";
import { decamelize } from "./utils/SnakeCamelConversion";

export interface TodosApiUrlOptions {
  variantId?: number;
  demo?: boolean;
  byUser?: boolean;
  userDecision: TodoUserDecision;
  category: TodoCategory | Array<TodoCategory>;
  isRecurring?: boolean;
}

declare const BACKEND_BASE_URL: string | undefined;
const BASE_URL = BACKEND_BASE_URL || "";

export enum OptiSupportEndpoints {
  DatenerfassungRedispatch = "datenerfassung-redispatch",
  EnergiedatenBereitstellen = "energiedaten-bereitstellen",
  EnergiedatenAusreichend = "energiedaten-ausreichend",
  BerechnungDesAbschopfungsbetrags = "berechnung-des-absch%C3%B6pfungsbetrags",
  BeruecksichtigungFiktiveMengen = "ber%C3%BCcksichtigung-fiktive-mengen",
  OnboardingProzessPVDirekt = "onboarding-prozess-pv-direktlieferung",
  GutschriftErstellenParagraph6 = "gutschrift-erstellen-paragraph-6",
  GutschriftenNummernKreiseParagraph6 = "erstellen-individueller-gutschriftsnummernkreise",
  KommunaleBeteiligungNachParagraph6Eeg = "kommunale-beteiligung-nach-6-eeg",
  ZahlungsinformationenZurKommunalenBeteiligungNach6Eeg = "erstellung-von-zahlungsinformationen-zur-kommunalen-beteiligung-nach-6-eeg",
  HinterlegenVonErzeugungsanteilen = "hinterlegen-von-erzeugungsanteilen",
  RegulatorischeAufgaben = "regulatorischen-aufgaben",
  ScadaDatenUntermessschnittstelle = "scada-daten/untermessschnittstelle",
  Meldedaten = "meldetypen-die-opti.node-ihnen-zur-verfügung-stellt",
  RueckerstattungNachParagraph6 = "6-eeg-r%C3%BCckerstattungsprozess",
  MieterstromVertraegeUndLieferungen = "mieterstrom#verträge-und-lieferungen",
  MieterstromRechnungen = "mieterstrom#rechnungen",
  VerklammerungVonAnlagen = "verklammerung-von-anlagen"
}

export enum OperatorFeatures {
  Basic = "basic",
  Stromsteuer = "stromsteuer",
  Paragraph6EEG = "paragraph_6_eeg",
  Abschoepfung = "abschoepfung",
  RevenueMonitoring = "erloesmonitoring",
  Invoices = "invoices",
  GuaranteeOfOrigin = "guarantee_of_origin",
  OptiCharge = "opticharge",
  PPAaaS = "ppaaas"
}

const urls = {
  startPage: startPageApi,
  api: {
    ssoConfig: () => "api/configuration/sso/",
    componentsBase: (siteId: number) =>
      appendQueryString(`api/components-base/`, { site: siteId }),
    energyDataAcquisitionTags: () => "api/mbk/energy-data-acquisition-tags/",
    identityList: () => "api/mbk/identity-list/",
    deleteFullMbk: (siteId: number) => `/api/mbk/${siteId}/delete-full-mbk/`,
    deleteFullMbkForYears: (siteId: number, years: ReadonlyArray<string>) =>
      appendQueryString(urls.api.deleteFullMbk(siteId), {
        years
      }),
    mbk: (siteId: number) => `/api/mbk/${siteId}/`,
    mieterstromMbk: (siteId: number) =>
      `/api/mbk/create-for-mieterstrom/${siteId}/`,
    onboardingAssistantDocuments: (siteId: number) =>
      appendQueryString("api/onboarding-assistant/onboarding-todo-documents/", {
        site: siteId
      }),
    uploadMsbVollmachtFile: (siteId: number) =>
      `api/onboarding-assistant/msb-documents/${siteId}/upload/`,
    uploadVermarkterVollmachtFile: (siteId: number) =>
      `api/onboarding-assistant/dataload-documents/${siteId}/upload/`,
    siteCanGenerateMsbDocuments: (siteId: number) =>
      appendQueryString(
        "api/onboarding-assistant/msb-documents/can-generate/",
        {
          site: siteId
        }
      ),
    vollmachtMsbFiles: (siteId: number) =>
      appendQueryString("api/onboarding-assistant/msb-documents/", {
        site: siteId
      }),
    vollmachtVermarkterFiles: (siteId: number) =>
      appendQueryString("api/onboarding-assistant/dataload-documents/", {
        site: siteId
      }),
    generateMsbVollmacht: (siteId: number) =>
      `/api/onboarding-assistant/msb-documents/${siteId}/generate-template/`,
    getMsbVollmacht: (siteId: number, taskId: string) =>
      appendQueryString(
        `/api/onboarding-assistant/msb-documents/${siteId}/generate-template/`,
        {
          task_id: taskId
        }
      ),
    siteCanGenerateVermarkterDocuments: (siteId: number) =>
      appendQueryString(
        "api/onboarding-assistant/dataload-documents/can-generate/",
        {
          site: siteId
        }
      ),
    generateVermarkterVollmacht: (siteId: number) =>
      `/api/onboarding-assistant/dataload-documents/${siteId}/generate-template/`,
    getVermarkterVollmacht: (siteId: number, taskId: string) =>
      appendQueryString(
        `/api/onboarding-assistant/dataload-documents/${siteId}/generate-template/`,
        {
          task_id: taskId
        }
      ),
    onboardingPhaseUpdateDone: () =>
      "api/onboarding-assistant/site-wizard/update-onboarding-phase-done/",
    onboardingProcessUpdateDone: () =>
      "api/onboarding-assistant/site-wizard/update-onboarding-process-done/",
    siteWizard: (siteId: number) =>
      `api/onboarding-assistant/site-wizard/${siteId}/`,
    energyWizard: (siteId: number) =>
      `api/onboarding-assistant/energy-wizard/${siteId}/`,
    energyWizardUpdate: (siteId: number) =>
      `api/onboarding-assistant/energy-wizard/${siteId}/update-done/`,
    customersList: () => "api/customers/",
    obersteVertriebspartnerList: () => "api/oberste_vertriebspartner/",
    projectsList: (isFilterActive?: boolean, siteId?: number) =>
      appendQueryString("api/projects/", { active: isFilterActive, siteId }),
    projectsBySiteId: (isFilterActive?: boolean, siteId?: number) =>
      appendQueryString("api/projects/", { active: isFilterActive, siteId }),
    projectsDetail: (projectId: string) => `api/projects/${projectId}/`,
    projectsShare: (projectId: string) =>
      `${urls.api.projectsDetail(projectId)}share/`,
    projectsRevoke: (projectId: string) =>
      `/api/projects/${projectId}/revoke_share/`,
    managerProjectsCreate: () => "/api/projects-create-manager/",
    setSiteInvoicing: (siteId: number, active: boolean) =>
      `api/sites/${siteId}/${active ? "activate" : "deactivate"}-invoicing/`,
    activateMeteringConceptV2: (siteId: number) =>
      `api/sites/${siteId}/activate-metering-concept-v2/`,
    deactivateMeteringConceptV2: (siteId: number) =>
      `api/sites/${siteId}/deactivate-metering-concept-v2/`,
    setSiteGo: (siteId: number, active: boolean) =>
      `api/sites/${siteId}/${active ? "activate" : "deactivate"}-go/`,
    setSiteLicense: (siteId: number, license: SiteLicense) =>
      `api/sites/${siteId}/set-license/${license}/`,
    setSitePremiumTodos: (siteId: number, active: boolean) =>
      `api/sites/${siteId}/${active ? "activate" : "deactivate"}-premium-todos/`,
    sketchData: (siteId: number) =>
      appendQueryString("api/sketch-data/", { site: siteId }),
    extendedSketchData: (siteId: number) =>
      appendQueryString("api/extended-sketch-data/", { site: siteId }),
    meteringConceptSketchData: (siteId: number) =>
      appendQueryString("api/metering-concept-sketch-data/", {
        site: siteId
      }),
    sketchElementsUpdate: (id: number | string) => `api/sketch-elements/${id}/`,
    sketchEdges: () => "/api/sketch-edges/",
    sketchEdgesDetail: (id: number) => `/api/sketch-edges/${id}/`,
    meteringConceptSketchEdges: () =>
      "/api/sketch-edges-with-connection-point-positions/",
    meteringServiceProviders: () => `/api/metering_service_providers/`,
    meteringServiceProvider: (id: number) =>
      `/api/metering_service_providers/${id}/`,
    variantDetail: (variantId: number) => `api/variants/${variantId}/`,
    exportProjectAsExcel: (variantId: number) =>
      `/api/variants/${variantId}/export-project-as-excel/`,
    regulatoryDutiesList: () => `api/regulatory-duties/`,
    todos: ({
      variantId,
      demo,
      userDecision,
      category,
      byUser,
      isRecurring
    }: TodosApiUrlOptions) =>
      appendQueryString("api/todos/", {
        variant: variantId,
        demo: demo,
        user_decision: userDecision,
        category: category,
        by_user: byUser,
        recurring: isRecurring
      }),
    systemCreatedTodos: (variantId: number) =>
      appendQueryString("api/todos/", {
        variant: variantId,
        system_created: "True"
      }),
    todosCreate: () => "api/todos/",
    todosDetail: (todoId: number) => `api/todos/${todoId}/`,
    todosUpdateList: () => `/api/todos/amend-todo-list-with-regulatory-duties/`,
    todoDocuments: () => "/api/todo-documents/",
    todoDocument: (documentId: number) => `/api/todo-documents/${documentId}/`,
    todoDocumentRename: (documentId: number) =>
      `/api/todo-documents/${documentId}/rename/`,
    todoDocumentRevoke: (documentId: number) =>
      `/api/todo-documents/${documentId}/revoke/`,
    todoDocumentDownload: (documentId: number) =>
      `/api/todo-documents/${documentId}/download/`,
    todoDownloadSelectedDocuments: (todoId: number) =>
      `/api/todos/${todoId}/download-selected-documents/`,
    todoDeleteSelectedDocuments: (todoId: number) =>
      `/api/todos/${todoId}/delete-selected-documents/`,
    todoGenerateDocuments: (todoId: number) =>
      `/api/todos/${todoId}/generate-documents/`,
    todoGenerateDocumentsAsFormAdmin: (todoId: number) =>
      `/api/todos/${todoId}/generate-documents-as-formadmin/`,
    todoGenerateProformaDocuments: (todoId: number) =>
      `/api/todos/${todoId}/generate-proforma-documents/`,
    todoDocumentProvided: (todoId: number) =>
      `/api/todos/${todoId}/update-document-provided/`,
    todoNotify: (todoId: number) => `/api/todos/${todoId}/notify/`,
    parameterSetCollection: () => "api/parameter-collection/",
    meterDataGaps: (
      firstDate: string,
      lastDate: string,
      unit: Unit,
      meter: number
    ) =>
      appendQueryString("api/meter-data-gaps/data/", {
        first_date: firstDate,
        last_date: lastDate,
        unit,
        meter
      }),
    locationDataGaps: (firstDate: string, lastDate: string, location: number) =>
      appendQueryString("api/location-data-gaps/", {
        first_date: firstDate,
        last_date: lastDate,
        location
      }),
    energyDataAcquisitions: (variantId: number) =>
      `/api/energy-data-acquisitions/?variant=${variantId}`,
    energyDataAcquisitionsBySite: (siteId: number, date: string) =>
      appendQueryString(`/api/energy-data-acquisitions/`, {
        date: date,
        sites: siteId
      }),
    energyDataAcquisitionsWithFilters: (variantId: number, filters: string) =>
      `${urls.api.energyDataAcquisitions(variantId)}${filters}`,
    energyDataDisplayData: () => `/api/energy-data-display/data/`,
    energyDataRawData: (
      meter: number,
      showMeterNamesInHeader: boolean,
      firstDate?: string,
      lastDate?: string,
      unit?: Unit
    ) =>
      appendQueryString("/api/meter-data-display/data/", {
        meter,
        show_meter_names_in_header: showMeterNamesInHeader,
        first_date: firstDate,
        last_date: lastDate,
        unit: unit
      }),
    energyDataRawLocationData: (
      location: number,
      showLocationNamesInHeader: boolean,
      firstDate?: string,
      lastDate?: string,
      unit?: Unit
    ) =>
      appendQueryString("/api/location-data-display/data/", {
        location,
        show_location_names_in_header: showLocationNamesInHeader,
        first_date: firstDate,
        last_date: lastDate,
        unit: unit
      }),
    energyDataRawDataRequestDownload: () =>
      `/api/metering-data-export/download/`,
    energyDataRawDataDownload: (taskId: string) =>
      appendQueryString(`/api/metering-data-export/download/`, {
        task_id: taskId
      }),
    energyDataAcquisitionFilters: (projectId?: string) =>
      appendQueryString("/api/energy-data-acquisition-filters/", {
        project: projectId
      }),
    energyDataAcquisitionFiltersDetail: (filterId: number) =>
      `/api/energy-data-acquisition-filters/${filterId}/`,
    energyDataAcquisitionFilterOptions: (projectId?: string) =>
      appendQueryString("/api/energy-data-acquisition-filter-options/", {
        project: projectId
      }),
    energyDataTemplateDownload: (
      variantId: number,
      firstDate: string,
      lastDate: string,
      selectedMeter: number | null,
      unit = Unit.KilowattHour
    ) =>
      appendQueryString(`/api/energy-data-transfer/template/`, {
        variant: variantId,
        first_date: firstDate,
        last_date: lastDate,
        selected_meter: selectedMeter,
        unit: unit
      }),
    energyDataTemplateMeters: (variantId: number) =>
      appendQueryString("/api/energy-data-transfer/meters-for-template/", {
        variant: variantId
      }),
    energyDataRequestDownload: () => `/api/energy-data-transfer/download/`,
    energyDataDownload: (taskId: string) =>
      appendQueryString(`/api/energy-data-transfer/download/`, {
        task_id: taskId
      }),
    energyDataUpload: () => "/api/data-import-requests/upload/",
    requestSupportForDataUpload: (requestId: string) =>
      `/api/data-import-requests/${requestId}/support/`,
    energyDataUploadFiles: () => "/api/data-import-requests/files/",
    fileUploadWithMeterRetry: (requestId: string) =>
      `/api/data-import-retry/${requestId}/meter/`,
    fileUploadWithOriginRetry: (requestId: string) =>
      `/api/data-import-retry/${requestId}/origin/`,
    fileUploadWithParameterSetRetry: (requestId: string) =>
      `/api/data-import-retry/${requestId}/parameter/`,
    energyDataUploadImportRequests: ({
      variant,
      page,
      page_size,
      ordering
    }: {
      variant?: number;
      page?: number;
      page_size?: number;
      ordering?: string;
    }) =>
      appendQueryString(
        `/api/data-import-requests/`,
        {
          variant,
          page,
          page_size,
          ordering: decamelize(ordering ?? "")
        },
        { skipNull: true, skipEmptyString: true }
      ),
    energyDataUploadImportRequest: (requestId: string) =>
      `/api/data-import-requests/${requestId}/`,
    energyDataUploadImportRequestDetails: (requestId: string) =>
      `/api/data-import-request-with-details/${requestId}/`,
    fileOriginsRequests: () => "api/file-origins/",
    energyDataSufficiencyAssessment: (
      siteId: number,
      year: number,
      taskId?: string
    ) =>
      appendQueryString("/api/data-sufficiency-assessment/", {
        site: siteId,
        year: year,
        task_id: taskId
      }),
    machineAccountConfigurations: (variant?: number) =>
      appendQueryString(
        "/api/machine-account-configuration/",
        { variant },
        { skipNull: true, skipEmptyString: true }
      ),
    machineAccountConfiguration: (id: number | string, variant?: number) =>
      appendQueryString(
        `/api/machine-account-configuration/${id}/`,
        { variant },
        { skipNull: true, skipEmptyString: true }
      ),
    reimportFormachineAccountConfiguration: (
      id: number | string,
      variant?: number
    ) =>
      appendQueryString(
        `/api/machine-account-configuration/${id}/reupload/`,
        { variant },
        { skipNull: true, skipEmptyString: true }
      ),
    gasConnectionOption: (gasConnectionId: number) =>
      `/api/gas-connections/${gasConnectionId}/gas-state-acquisition-data/`,
    paragraph6CreateCredit: () =>
      `/api/paragraph-6-eeg-credit-generation/start-generation/`,
    paragraph6DraftCreditStatus: () =>
      `/api/paragraph-6-eeg-credit-generation/get-draft-credit/`,
    generators: (args: { siteId?: number; personId?: number }) =>
      appendQueryString(`/api/generators/`, {
        site: args.siteId,
        person: args.personId
      }),
    generator: (generatorId: number) => `/api/generators/${generatorId}/`,
    calculateVBH: (componentId: number, years: Array<number>) =>
      appendQueryString(`api/kwkanlage/calculate-vbh/`, {
        anlage: componentId,
        years: years
      }),
    importGeneratorsFromMastr: (siteId?: number) =>
      appendQueryString("/api/mastr-generators/", { site: siteId }),
    getCreatedGeneratorsFromMastr: (taskId: string) =>
      appendQueryString(
        "/api/mastr-generators/get-created-generator-from-mastr-result/",
        { task_id: taskId }
      ),
    updateGeneratorWithChangesFromMastr: (generatorId: number) =>
      `/api/mastr-generators/${generatorId}/update-generator-with-changes-from-mastr/`,
    getChangesFromMastr: (generatorId: number) =>
      `/api/mastr-generators/${generatorId}/get-changes-from-mastr/`,
    getChangesFromMastrResult: (taskId: string) =>
      appendQueryString(
        "/api/mastr-generators/get-changes-from-mastr-result/",
        {
          task_id: taskId
        }
      ),
    uploadGeneratorBatchImport: (
      siteId: number,
      clean?: boolean,
      ignoreOtherSites?: boolean
    ) =>
      appendQueryString(`/api/batch-mastr-generators/batch_import/`, {
        site: siteId,
        clean,
        ignore_other_sites: ignoreOtherSites
      }),
    getCreatedGeneratorsFromBatchImport: (taskId: string) =>
      appendQueryString("/api/batch-mastr-generators/get_created/", {
        task_id: taskId
      }),
    operationPeriods: (componentId: number) =>
      appendQueryString(`/api/operation-periods/`, { component: componentId }),
    operationPeriodsMeteringReadingsRequired: (componentId: number) =>
      appendQueryString(`/api/operation-periods/meter_readings_required/`, {
        component: componentId
      }),
    seeWind: (id: number) => `/api/seewind/${id}/`,
    consumers: (siteId: number) =>
      appendQueryString(`/api/consumers/`, { site: siteId }),
    consumer: (consumerId: number) => `/api/consumers/${consumerId}/`,
    siteStatusOverview: () => "/api/site-status-overview/",
    siteStatusOverviewByYear: (year: number) =>
      `/api/site-status-overview/${year}/`,
    siteOverview: () => "/api/site-overview/",
    storages: (siteId: number) =>
      appendQueryString(`/api/storages/`, { site: siteId }),
    storage: (storageId: number) => `/api/storages/${storageId}/`,
    personsByVariantId: (variantId: number) =>
      appendQueryString(`/api/persons/`, { variant: variantId }),
    personsBySiteId: (siteId: number) =>
      appendQueryString(`/api/persons/`, { site: siteId }),
    person: (personId: number) => `/api/persons/${personId}/`,
    uploadPersonImage: (personId: number, fieldName: string) =>
      appendQueryString(`/api/persons/${personId}/upload_image/`, {
        field: fieldName
      }),
    deletePersonImage: (personId: number, fieldName: string) =>
      appendQueryString(`/api/persons/${personId}/delete_image/`, {
        field: fieldName
      }),
    importPersonsFromMastr: () => "/api/mastr-companies/",
    getCreatedPersonFromMastr: (taskId: string) =>
      appendQueryString("/api/mastr-companies/", { task_id: taskId }),
    connections: (siteId: number) =>
      appendQueryString(`/api/connections/`, { site: siteId }),
    meters: (siteId: number) =>
      appendQueryString(`/api/meters/`, { site: siteId }),
    standaloneSketchUpdate: (nodeId: string) =>
      `api/sketch-elements-with-relative-positions/${nodeId}/`,
    standaloneSketchElementsTextBlock: () =>
      `api/standalone-sketch-elements/text-blocks/`,
    standaloneSketchElementsTextBlockOptions: (textBlockId: string) =>
      `api/standalone-sketch-elements/text-blocks/${textBlockId}/`,
    standaloneSketchElementContainer: () =>
      `api/standalone-sketch-elements/containers/`,
    standaloneSketchElementsContainerOptions: (containerId: string) =>
      `api/standalone-sketch-elements/containers/${containerId}/`,
    meterParameters: (configurationId: number) =>
      `/api/sub-metering-configurations/${configurationId}/get-meter-parameters/`,
    updateOrCreateMeterParameters: (configurationId: number) =>
      `/api/sub-metering-configurations/${configurationId}/update-or-create-meter-parameters/`,
    findMeterSuggestions: (configurationId: number) =>
      `/api/sub-metering-configurations/${configurationId}/find-meter-suggestions/`,
    getMeterSuggestions: (taskId: string) =>
      appendQueryString(
        `/api/sub-metering-configurations/get-meter-suggestions/`,
        { task_id: taskId }
      ),
    meter: (meterId: number) => `/api/meters/${meterId}/`,
    consumptionShare: (consumptionShareId?: number) =>
      `/api/consumption-shares/${consumptionShareId ?? ""}`,
    ghostNodes: (siteId: number) =>
      appendQueryString(`api/ghost-nodes/`, { site: siteId }),
    ghostNode: (ghostNodeId: number) => `api/ghost-nodes/${ghostNodeId}/`,
    meteringLocations: (siteId: number) =>
      appendQueryString("/api/metering-locations/", { site: siteId }),
    meteringLocation: (meteringLocationId: number) =>
      `/api/metering-locations/${meteringLocationId}/`,
    marketLocations: (siteId: number) =>
      appendQueryString("/api/market-locations/", { site: siteId }),
    marketLocation: (marketLocationId: number) =>
      `/api/market-locations/${marketLocationId}/`,
    meteringOrMarketLocations: (siteId?: number, variantId?: number) =>
      appendQueryString("/api/metering-or-market-locations/", {
        site: siteId,
        variant: variantId
      }),
    meteringOrMarketLocation: (meteringOrMarketLocationId: number) =>
      `/api/metering-or-market-locations/${meteringOrMarketLocationId}/`,
    meteringOrMarketLocationsOverview: (
      queryParams: LocationOverviewDataQueryParams | null
    ) =>
      appendQueryString(
        "/api/metering-or-market-locations-overview/info/",
        queryParams as unknown as { [key: string]: unknown },
        {
          arrayFormat: "comma"
        }
      ),
    meteringOrMarketLocationsSuggestedCommissionPeriod: (
      meteringOrMarketLocationId: number
    ) =>
      `/api/metering-or-market-locations/${meteringOrMarketLocationId}/suggested-commission-period/`,
    suggestedMeteringOrMarketLocation: (generatorId: number) =>
      appendQueryString(
        "/api/einspeisezeitreihe-melo-malo/suggest_abrechnungsrelevante_einspeisezeitreihe_melo_malo/",
        { generator: generatorId }
      ),
    downloadSelectedTodoDocuments: (variantId: number) =>
      `api/variants/${variantId}/download-selected-todos-documents/`,
    updateTodoStatus: (todoId: number) => `/api/todos/${todoId}/update_status/`,
    updateTodoResponsible: (todoId: number) =>
      `/api/todos/${todoId}/update_responsible/`,
    updateTodoPriority: (todoId: number) =>
      `/api/todos/${todoId}/update_priority/`,
    updateTodoUserDecisions: () => `/api/todos/update-user-decisions/`,
    updateTodoDueDate: (todoId: number) =>
      `/api/todos/${todoId}/update_due_date/`,
    updateTodoPerson: (todoId: number) => `/api/todos/${todoId}/update_person/`,
    updateTodoOverdue: (todoId: number) =>
      `/api/todos/${todoId}/update_overdue/`,
    todoRuleAssessment: (todoId: number) =>
      `/api/todos/${todoId}/trigger-rule-assessment/`,
    todoCalendarIntegrations: (variantId: number) =>
      appendQueryString(`/api/todo-calendars/`, { variant: variantId }),
    todoCalendarIntegrationDetail: (calendarIntegrationId: number) =>
      `/api/todo-calendars/${calendarIntegrationId}/`,
    todoCalendarIntegrationRefresh: (calendarIntegrationId: number) =>
      `/api/todo-calendars/${calendarIntegrationId}/refresh/`,
    settings: () => "/api/settings/",
    meterEnergyData: () => `api/update-meter-energy-data/`,
    counterDataForGasConnection: (gasConnectionId: number) =>
      `/api/gas-state-acquisition/${gasConnectionId}/data/`,
    dateRange: (params) => appendQueryString("api/date-range/", params),
    changePassword: () => "/api/users/me/change-password/",
    currentUserCanAssessManagerSiteFinancially: () =>
      "/api/users/can-assess-manager-site-financially/",
    anlagenStromstgDetailUrl: (id: number) => `/api/anlagestromstg/${id}/`,
    anlageStromstgSetGenerators: (id: number) =>
      `/api/anlagestromstg/${id}/set-generators/`,
    anlagenStromstgListGenerators: (id: number) =>
      `/api/anlagestromstg/${id}/list-generators/`,
    subMeteringConfigurations: (variantId: number) =>
      appendQueryString("/api/sub-metering-configurations/", {
        variant: variantId
      }),
    subMeteringConfiguration: (id: number) =>
      `/api/sub-metering-configurations/${id}/`,
    subMeteringConfigurationMeterAssignments: (configurationId: number) =>
      `/api/sub-metering-configurations/${configurationId}/set-meter-assignments/`,
    subMeteringConfigurationHistoricalDataImport: (configurationId: number) =>
      `/api/sub-metering-configurations/${configurationId}/trigger_data_import/`,
    invoices: (projectId: string, isArchived?: boolean) =>
      appendQueryString("/api/invoices/", {
        projectId: projectId,
        archived: isArchived
      }),
    generateInvoices: (projectId: string) =>
      appendQueryString("/api/invoices/generate_invoices/", {
        projectId: projectId
      }),
    generateSingleInvoice: () => `/api/invoices/generate_invoice/`,
    generatePreviewInvoices: () => `/api/invoice-preview/`,
    getPreviewInvoice: (contractId: string, taskId: string) =>
      appendQueryString(`/api/invoice-preview/`, {
        task_id: taskId,
        contract: contractId
      }),
    invoice: (id: string) => `/api/invoices/${id}`,
    archiveInvoice: (id: string) => `/api/invoices/${id}/reverse/`,
    activeCompanies: (variantId: number) =>
      `/api/variants/${variantId}/active-companies/`,
    downloadInvoice: (id: string) => `/api/invoices/${id}/download/`,
    invoiceUpdateState: (id: string) => `/api/invoices/${id}/update_state/`,
    sendInvoiceToSupplied: (id: string) =>
      `/api/invoices/${id}/send_invoice_to_supplied/`,
    advancePaymentsPlans: (projectId: string) =>
      appendQueryString("/api/advance-payments-plans/not-archived/", {
        projectId: projectId
      }),
    advancePaymentsPlan: (id: string) => `/api/advance-payments-plans/${id}/`,
    generateAdvancePaymentsPlans: (projectId: string, year: number) =>
      appendQueryString(
        "/api/advance-payments-plans/generate_advance_payments_plans/",
        { projectId: projectId, year }
      ),
    archivedAdvancePaymentsPlans: (projectId: string) =>
      appendQueryString("/api/advance-payments-plans/archived/", {
        projectId: projectId
      }),
    archiveAdvancePaymentsPlan: (id: string) =>
      `/api/advance-payments-plans/${id}/archive/`,
    downloadAdvancePaymentsPlan: (id: string) =>
      `/api/advance-payments-plans/${id}/download/`,
    fictionalEnergyValues: (variantId: number) =>
      appendQueryString(`/api/fiktive-strommengen/`, {
        variant: variantId
      }),
    fictionalEnergyValue: (id: string) => `/api/fiktive-strommengen/${id}/`,
    deliveries: (projectId: string) =>
      appendQueryString("/api/deliveries/", {
        projectId: projectId
      }),
    deliveryFormOptions: (projectId: string) =>
      appendQueryString("/api/deliveries/get-choices/", { project: projectId }),
    delivery: (id: string) => `/api/deliveries/${id}/`,
    deliverySpotMarketPricesAvailable: (siteId: number) =>
      appendQueryString(`/api/deliveries/are-spot-market-prices-available/`, {
        site: siteId
      }),
    jobStatus: (taskId: string) =>
      appendQueryString("/api/job/status/", { task_ids: taskId }),
    direktvermarkter: () => "/api/direktvermarkter/",
    checkSubMeteringConnection: (configId: number) =>
      `/api/sub-metering-configurations/${configId}/check-connection/`,
    guaranteeOfOriginSites: (variant: number) =>
      appendQueryString("/api/guarantee-of-origin-sites/", {
        variant
      }),
    dispatchGuaranteeOfOriginCheck: () =>
      "/api/guarantee-of-origin-generation/start-data-check/",
    fetchGuaranteeOfOriginCheck: (taskId?: string) =>
      appendQueryString("/api/guarantee-of-origin-generation/check/", {
        task_id: taskId
      }),
    dispatchGuaranteeOfOriginGeneration: () =>
      "/api/guarantee-of-origin-generation/start-generation/",
    fetchGuaranteeOfOriginDocument: (taskId: string) =>
      appendQueryString("/api/guarantee-of-origin-generation/document/", {
        task_id: taskId
      }),
    mieterstromBatchImport: {
      dispatchCheck: () => "/api/mieterstrom-batch-import/start-check/",
      fetchCheck: (taskId: string) =>
        appendQueryString("/api/mieterstrom-batch-import/check/", {
          task_id: taskId
        }),
      dispatchSiteCreation: () =>
        "/api/sites/create-with-mieterstrom-template/",
      fetchSite: (taskId?: string) =>
        appendQueryString("/api/sites/create-with-mieterstrom-template/", {
          task_id: taskId
        })
    },
    mieterstromProcesses: {
      listOperatorChanges: (siteId: number) =>
        `/api/mieterstrom-processes/site-operator-change-processes/${siteId}/`,
      updateOperatorChanges: (siteId: number) =>
        `/api/mieterstrom-processes/site-operator-change-processes/${siteId}/update-done/`,
      listTenantChanges: (siteId: number) =>
        `/api/mieterstrom-processes/site-tenant-change-processes/${siteId}/`,
      updateTenantChanges: (siteId: number) =>
        `/api/mieterstrom-processes/site-tenant-change-processes/${siteId}/update-done/`
    },
    featureFlags: () => "/api/feature-flags/",
    gridOperators: () => "/api/grid_operators/",
    meteringConcepts: () => "/api/metering-concepts/",
    // Needs the fully-qualified URL because the Vercel AI SDK's transport
    // fetches this directly instead of going through our API client, which is
    // what normally resolves relative paths against the backend base URL.
    aiChat: () => `${backendBaseUrl.replace(/\/$/, "")}/api/ai-chat/`
  },

  apiWithoutCamelization: {
    consumer: (consumerId: number) => `/api/consumers/${consumerId}/`,
    consumers: (siteId: number) =>
      appendQueryString(`/api/consumers/`, { site: siteId }),
    connection: (connectionId: number) => `/api/connections/${connectionId}/`,
    connections: (siteId: number) =>
      appendQueryString(`/api/connections/`, { site: siteId }),
    gasConnection: (gasConnectionId: number) =>
      `/api/gas-connections/${gasConnectionId}/`,
    gasConnections: (siteId: number) =>
      appendQueryString(`/api/gas-connections/`, { site: siteId }),
    meter: (meterId: number) => `/api/meters/${meterId}/`,
    meters: (siteId: number) =>
      appendQueryString(`/api/meters/`, { site: siteId }),
    meteringConceptMeter: (meterId: number) =>
      `/api/metering-concept-meter-fields/${meterId}/`,
    mkExport: () => `/api/mk-export/`,
    mkExportFinished: (taskId: string) =>
      appendQueryString("/api/mk-export/", {
        task_id: taskId
      }),
    operators: (args: {
      project_id?: string;
      variant_id?: number;
      activeFeatures?: Array<OperatorFeatures>;
      withDetails?: boolean;
    }) => appendQueryString("/api/configurator/operators/", args),
    sites: ({
      variantId,
      projectId
    }: {
      variantId?: number;
      projectId?: string;
    }) =>
      appendQueryString("api/sites/", {
        variant: variantId,
        projectId: projectId
      }),
    sitesList: (variantId?: number) =>
      urls.apiWithoutCamelization.sites({ variantId }),
    sitesDetail: (siteId: number) => `api/sites/${siteId}/`,
    todoNoteCreate: () => "/api/todo-notes/",
    todoNotesDetail: (noteId: number) => `/api/todo-notes/${noteId}/`,
    todoNoteUpdate: (noteId: number) =>
      `${urls.apiWithoutCamelization.todoNotesDetail(noteId)}perform-todo-note-update/`,
    todoNotes: (todoId: number) =>
      appendQueryString("/api/todo-notes/", {
        todo: todoId
      }),
    generatorSingleEndpoint: (generatorId: number) =>
      `/api/generators-single-endpoint/${generatorId}/`,
    generatorSingleEndpointList: (
      siteId?: number,
      vermarktungsvertragCompanyId?: number
    ) =>
      appendQueryString(`/api/generators-single-endpoint/`, {
        site: siteId,
        vermarktungsvertrag_company: vermarktungsvertragCompanyId
      }),
    validateSEENumber: () => "/api/site-setup-assistant/validate-pv-plant/",
    meteringConceptTodos: (siteId: number) =>
      appendQueryString("api/onboarding-assistant/metering-concept-todos/", {
        site: siteId
      }),
    consumerHistory: (consumerId: number) =>
      `api/consumers/${consumerId}/get-history/`,
    contractHistory: (id: string) => `/api/contracts/${id}/get-history/`,
    currentUser: () => "/api/users/me/",
    rlmGridFees: (siteId: number) =>
      appendQueryString(`/api/rlm-grid-fees/`, { site: siteId }),
    slpGridFees: (siteId: number) =>
      appendQueryString(`/api/slp-grid-fees/`, { site: siteId }),
    deliveryHistory: (id: string) => `api/deliveries/${id}/get-history/`,
    paragraph6ContractHistory: (id: string) =>
      `api/paragraph-6-eeg-contracts/${id}/get-history/`,
    paragraph6GemeindeAnteilErzeugerHistory: (
      contractId: string,
      generatorId: string
    ) =>
      `api/paragraph-6-eeg-contracts/${contractId}/gemeinde-anteile-erzeuger/${generatorId}/get-history/`,
    direktvermarktungContractHistory: (id: string) =>
      `api/direktvermarktungsvertraege/${id}/get-history/`,
    direktvermarktungContracts: ({
      variantId,
      contractId,
      activeOnly,
      expiredOnly,
      upcomingOnly
    }: {
      variantId?: string;
      contractId?: string;
      activeOnly?: boolean;
      expiredOnly?: boolean;
      upcomingOnly?: boolean;
    } = {}) =>
      appendQueryString(`/api/direktvermarktungsvertraege/`, {
        variant_id: variantId,
        contract_id: contractId,
        active: activeOnly,
        expired: expiredOnly,
        upcoming: upcomingOnly
      }),
    direktvermarktungContract: (contractId: string) =>
      `/api/direktvermarktungsvertraege/${contractId}/`,
    direktvermarktungUnassignedGenerators: (contractUuid: string) =>
      `/api/direktvermarktungsvertraege/${contractUuid}/unassigned-generators/`,
    direktvermarktungGeneratorShare: (
      contractUuid: string,
      generatorId: number
    ) =>
      `/api/direktvermarktungsvertraege/${contractUuid}/generation-shares/${generatorId}/`,
    direktvermarktungContractExtension: (contractUuid: string) =>
      `/api/direktvermarktungsvertraege/${contractUuid}/extend/`,
    generatorHistory: (generatorId: number) =>
      `api/generators/${generatorId}/get-history/`,
    generatorsByVariant: (variantId: number) =>
      appendQueryString(`/api/generators/`, { variant: variantId }),
    meterHistory: (meterId: number) => `/api/meters/${meterId}/get-history/`,
    meteringOrMarketLocationsHistory: (id: number) =>
      `/api/metering-or-market-locations/${id}/get-history/`,
    meteringServiceProviderHistory: (id: number) =>
      `/api/metering_service_providers/${id}/get-history/`,
    oauthApplication: (applicationId: string) =>
      `/api/oauth/applications/${applicationId}/`,
    oauthApplications: () => "/api/oauth/applications/",
    oauthApplicationRegenerateClientSecret: (applicationId: string) =>
      `/api/oauth/applications/${applicationId}/regenerate-client-secret/`,
    oauthApplicationsOptionsForCreate: () =>
      "/api/oauth/applications/options-for-create/",
    paragraph6Companies: (variantId: number) =>
      `/api/paragraph-6-eeg-contracts/list-companies/?variant=${variantId}`,
    paragraph6Contracts: () => `/api/paragraph-6-eeg-contracts/`,
    paragraph6Contract: (contractId: string) =>
      `/api/paragraph-6-eeg-contracts/${contractId}/`,
    paragraph6Credit: (creditId: string) =>
      `/api/paragraph-6-eeg-credits/${creditId}/`,
    paragraph6CreditDocument: (creditId: string) =>
      `${urls.apiWithoutCamelization.paragraph6Credit(creditId)}download/`,
    paragraph6CreditUpdateState: (creditId: string) =>
      `${urls.apiWithoutCamelization.paragraph6Credit(creditId)}update-state/`,
    paragraph6CreditGetData: (creditId: string) =>
      `${urls.apiWithoutCamelization.paragraph6Credit(creditId)}get-credit-data/`,
    paragraph6Cancellation: (cancellationId: string) =>
      `/api/paragraph-6-eeg-cancellations/${cancellationId}/`,
    paragraph6CancellationUpdateState: (cancellationId: string) =>
      `${urls.apiWithoutCamelization.paragraph6Cancellation(cancellationId)}update-state/`,
    paragraph6Cancellations: (variantId: number) =>
      appendQueryString(`/api/paragraph-6-eeg-cancellations/`, {
        variant_id: variantId
      }),
    paragraph6CreateCancellation: () =>
      `/api/paragraph-6-eeg-cancellation-generation/start-generation/`,

    paragraph6CancellationDocument: (cancellationId: string) =>
      `${urls.apiWithoutCamelization.paragraph6Cancellation(cancellationId)}download/`,
    paragraph6GemeindeAnteileErzeuger: (contractId: string) =>
      `/api/paragraph-6-eeg-contracts/${contractId}/gemeinde-anteile-erzeuger/`,
    paragraph6GemeindeAnteilErzeuger: (
      contractId: string,
      generatorId: number
    ) =>
      `/api/paragraph-6-eeg-contracts/${contractId}/gemeinde-anteile-erzeuger/${generatorId}/`,
    paragraph6UnassignedGenerators: (contractId: string) =>
      `/api/paragraph-6-eeg-contracts/${contractId}/unassigned-generators/`,
    paragraph6VariantContracts: (variantId: number) =>
      appendQueryString(urls.apiWithoutCamelization.paragraph6Contracts(), {
        variant_id: variantId
      }),
    paragraph6VariantCredits: (variantId: number) =>
      appendQueryString("/api/paragraph-6-eeg-credits/", {
        variant: variantId
      }),
    paragraph6CreatableCredits: (variantId: number) =>
      appendQueryString(
        `/api/paragraph-6-eeg-credit-generation/list-creatable-credits/`,
        { variant: variantId }
      ),
    paragraph6CreditErrors: (variantId: number) =>
      appendQueryString(
        "/api/paragraph-6-eeg-credit-generation/list-non-creatable-credits/",
        { variant: variantId }
      ),
    paragraph6CreditNumberRange: (creditNumberRangeId: string) =>
      `/api/paragraph-6-eeg-credit-number-ranges/${creditNumberRangeId}/`,
    paragraph6CreditNumberRanges: (variantId: number) =>
      appendQueryString(`/api/paragraph-6-eeg-credit-number-ranges/`, {
        variant_id: variantId
      }),
    personHistory: (personId: number) =>
      `/api/persons/${personId}/get-history/`,
    revenueMonitoringPerPerson: (args: { personId: number }) =>
      appendQueryString("/api/erloesmonitoring/revenue-for-person/", args),
    revenueMonitoringPerGenerator: (args: { generatorIds: Array<number> }) =>
      appendQueryString("/api/erloesmonitoring/revenue-for-generators/", args),
    revenueMonitoringCellChecks: (personId?: number) =>
      appendQueryString("/api/erloesmonitoring/cell-checks/", {
        personId
      }),
    revenueMonitoringCellCheck: (stateId: string) =>
      `/api/erloesmonitoring/cell-checks/${stateId}/`,
    revenueMonitoringCellCheckHistory: (stateId: string) =>
      `/api/erloesmonitoring/cell-checks/${stateId}/get-history/`,
    ausfallarbeitSteuersignaleUpload: () => "/api/ausfallarbeit/steuersignale/",
    ausfallarbeitTimeSeries: (args: AusfallarbeitTimeSeriesQueryParams) =>
      appendQueryString("/api/ausfallarbeit/time-series/", args),
    siteCategories: (projectId: string) =>
      appendQueryString("api/site-categories/", {
        project: projectId
      }),
    energyDataAvailability: (
      site: number,
      firstDate: string,
      lastDate: string
    ) =>
      appendQueryString("/api/energy-data-availability/", {
        site,
        first_date: firstDate,
        last_date: lastDate
      }),
    siteCategoryForSite: (siteId: number) =>
      appendQueryString("api/site-categories/", {
        site: siteId
      }),
    siteCategoriesSummary: () => "/api/site-categories-summary/",
    siteNotifications: (siteId: number) => `api/site-notifications/${siteId}/`,
    sitePlausibility: (siteId: number, start: string, end: string) =>
      appendQueryString("/api/site-plausibility/", {
        site: siteId,
        start,
        end
      }),
    systemUsers: () => "/api/users/system/",
    todoCanGenerateDocuments: (todoId: number) =>
      `/api/todos/${todoId}/can-generate-documents/`,
    userProfile: () => "/api/profile/",
    advancePaymentsCalculate: (contractId: string, year: number) =>
      appendQueryString(`api/advance-payments-calculate/${contractId}/`, {
        year
      }),
    siteSetupAssistant: {
      validateMissingMieterstromData: (siteSetupProcessId: string) =>
        `api/site-setup-assistant/validate-missing-mieterstrom-data/${siteSetupProcessId}/`,
      validatePlausibilityMieterstromData: (siteSetupProcessId: string) =>
        `api/site-setup-assistant/validate-plausiblilty-mieterstrom-data/${siteSetupProcessId}/`,
      createConsumers: () => `/api/site-setup-assistant/create-consumers/`,
      mieterStromData: (siteSetupProcessId: string) =>
        `/api/site-setup-assistant/mieterstrom-data/${siteSetupProcessId}/`,
      siteSetupProcesses: (variantId: number) =>
        appendQueryString("api/site-setup-assistant/site-setup-processes/", {
          variant: variantId
        }),
      siteSetupProcess: (siteSetupProcessId: string) =>
        `/api/site-setup-assistant/site-setup-processes/${siteSetupProcessId}/`,
      creation: (siteSetupProcessId: string, taskId?: string) =>
        appendQueryString(
          `/api/site-setup-assistant/mieterstrom-create/${siteSetupProcessId}/`,
          { task_id: taskId }
        ),
      uploadDocument: (siteSetupProcessId: string) =>
        `/api/site-setup-assistant/upload-document/${siteSetupProcessId}/`
    },
    variantsByProject: (projectId: string) =>
      appendQueryString("/api/variants/", { projectId }),
    variantDetail: (variantId: number) => `api/variants/${variantId}/`,
    generationShares: (siteId: number) =>
      appendQueryString("/api/generation-shares/", { site_id: siteId }),
    generationSharesDetail: (generator_id: number) =>
      `/api/generation-shares/${generator_id}/`,

    subMeteringConfigurationImportLogs: (
      configurationId: number,
      queryOptions: { offset: number; limit: number }
    ) =>
      appendQueryString(
        `/api/sub-metering-configurations/${configurationId}/sub-metering-logs/`,
        queryOptions
      ),
    quickSearch: () => "/api/quick-search/",
    confirmVermarkterVollmachtFile: () =>
      "api/onboarding-assistant/dataload-documents/confirm/"
  },

  optiSupport: (endpoint?: string) =>
    `https://support.node.energy/knowledge/${endpoint ?? ""}`,
  optiCharge: () => "https://charge.node.energy/",
  auth: {
    login: () => "/auth/login/",
    loginWithCode: () => "/auth/login/code/",
    logout: () => "/auth/logout/",
    refresh: () => "/auth/refresh/",
    activate2FaMethod: (method: string) => `/auth/${method}/activate/`,
    confirm2FaMethod: (method: string) => `/auth/${method}/activate/confirm/`,
    deactivate2FaMethod: () => "/auth/mfa/deactivate/",
    active2FaMethods: () => "/auth/mfa/user-active-methods/",
    generate2FaCode: () => "/auth/code/request/",
    resetPassword: () => "/auth/password-reset/",
    resetPasswordConfirm: () => "/auth/password-reset/confirm/",
    setPasswordConfirm: () => "/auth/set-password/confirm/",
    validateToken: () => "/auth/password-reset/validate-token/",
    microsoftLogin: () => `${BASE_URL}/microsoft/login`,
    roedlLogin: () => `${BASE_URL}/aad/roedl/login`
  },
  managerProjectView: (projectId: string) =>
    generatePath(ROUTES.project.entry, { projectId: projectId }),
  siteStatusPage: (siteId: number) => `/site-status/${siteId}`,
  siteStatusPageByYear: (siteId: number, year: number) =>
    `/site-status/${siteId}/${year}`
};

export default urls;
